import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, MouseEvent, ReactNode, RefObject, TouchEvent } from "react";

import EmptyState from "../components/states/EmptyState";
import ErrorState from "../components/states/ErrorState";
import Skeleton from "../components/states/Skeleton";
import {
  AWAITING_WORKBOOK_COPY,
  BLOCK_ORDER,
  TTM_MONTHS,
  WORKBOOK_UNAVAILABLE_COPY,
  blockCsv,
  chartScale,
  csvFileName,
  fmtAxis,
  fmtValue,
  formatReceived,
  groupRows,
  heroCards,
  itemCellText,
  itemValue,
  labelStride,
  monthLabel,
  noteFromLabel,
  parseTarget,
  provenanceTag,
  seriesSegments,
  truncatedCopy,
  ttm,
} from "../lib/execKpi.mjs";
import type {
  ExecKpiGrouped,
  ExecKpiItem,
  ExecKpiRow,
  ExecKpiSheet,
  HeroCard as HeroCardData,
} from "../lib/execKpi.d.mts";
import { ExecKpiFetchError, fetchExecKpiRows, useExecKpiAvailability } from "../lib/execKpiData";
import {
  CARD_ROW_LABEL,
  DIFFERENCE_ROW_LABEL,
  DRILL_CAVEAT,
  NOT_LOADED_CAPTION,
  NOT_LOADED_TITLE,
  READ_FAILED_CAPTION,
  READ_FAILED_TITLE,
  TOTAL_ROW_LABEL,
  cardNote,
  differenceNote,
  drillPanel,
  indexDrill,
  isDrillable,
} from "../lib/boardDrill.mjs";
import type { BoardDrillRow, DrillEntry, DrillPanel } from "../lib/boardDrill.d.mts";
import { BoardDrillFetchError, fetchBoardDrill } from "../lib/boardDrillData";
import { isStaff, useAuth } from "../lib/auth";
import {
  SOURCE_LABELS,
  applyLineSources,
  sourceCounts,
  staffCheck,
} from "../lib/execLineSource.mjs";
import { fetchExecLineSourceRows } from "../lib/execLineSourceData";
import {
  AWAITING_CARD_COPY,
  AWAITING_IMPORTS_COPY,
  DERIVED_STAMP,
  DERIVED_UNAVAILABLE_COPY,
  awaitingSentence,
  derivedToExecRows,
} from "../lib/execDerived.mjs";
import { fetchExecDerivedRows } from "../lib/execDerivedData";
import { executiveEntity } from "../lib/tabFreshness.mjs";
import { LiveCache, useLiveCacheVersion } from "../lib/liveCacheHook";
import { chipWords, useLiveReport, type LiveTabUpdatedAt } from "../lib/liveReport";
import { useTenantId } from "../lib/tenant";
import { domainVar } from "../lib/widgetDomain.mjs";

/**
 * MER-674: the Executive board page.
 *
 * The automated form of the replica AJ approved on 2026-09-14, rendered with
 * the dashboard's own tokens: the workbook's sheets as a button group
 * (rollup first), the four block pills, the five-card scorecard with
 * month-over-month deltas, a trend for any line item with the workbook's
 * own targets drawn as reference lines, and the full month-by-month table
 * with a TTM column and the note column. Per-block CSV export through a
 * normal browser download.
 *
 * Bespoke like Insights: no manifest tab, no resolver, no Zoho. Client
 * visible and not tier-locked.
 *
 * S1-exec-from-imports: each LINE shows exactly one source, and says which.
 * Where the pipeline computes the line from the client's imports
 * (public.exec_line_source, migration 0082) the line shows Meridian's figure,
 * labelled "From your imports"; every other line shows the client's own
 * workbook (public.exec_kpi, MER-667), labelled "From your workbook". The two
 * are never mixed inside a line. Staff sessions also see the workbook figure
 * under each imported line as the check. The switch never blocks the page:
 * if the source table is missing or unreadable, every line keeps the
 * workbook, which is the page as it was before.
 *
 * Three states a Delta user can meet: the workbook (ready), the awaiting
 * card when the tenant has no rows or the table is not there yet, and a
 * fixed-copy error card when the read fails for any other reason. The raw
 * database message never reaches the page; it goes to console.warn.
 *
 * All the arithmetic (grouping, deltas, TTM, formatting, targets, axis
 * scale, gaps, CSV) lives in lib/execKpi.mjs so node --test covers it
 * without React; this file is layout and state.
 */

type PageState =
  | { kind: "loading" }
  | { kind: "awaiting" }
  | { kind: "error" }
  | { kind: "ready"; rows: ExecKpiRow[]; truncated: boolean };

/**
 * S1-exec-from-imports: the workbook rows with each line's shown source
 * applied. A failed or missing source read keeps every line on the workbook
 * (labelled), never blocks the page.
 */
async function withLineSources(
  sourcesRead: Promise<ExecLineSourceRead>,
  rows: ExecKpiRow[],
): Promise<ExecKpiRow[]> {
  try {
    const sources = await sourcesRead;
    return applyLineSources(rows, sources.rows);
  } catch (err: unknown) {
    console.warn(
      `exec_line_source read failed: ${err instanceof Error ? err.message : String(err)}`,
    );
    return applyLineSources(rows, []);
  }
}
type ExecLineSourceRead = Awaited<ReturnType<typeof fetchExecLineSourceRows>>;

/**
 * AJ 2026-09-30 live refresh. Each Executive read goes through a LiveCache so
 * a newer EXEC_KPI / EXEC_DERIVED sync can be re-read in the background and
 * swapped in place (useLiveReport).
 *
 * AJ 2026-10-01 (speed): a mount within EXEC_REUSE_MS of the last read draws
 * from it instead of reading again (coming back to the Executive from another
 * tab cost the full read every time, about 2 s for Delta); useLiveReport
 * checks the sync record on the way back in and swaps a newer sync in. An
 * older read is dropped and read fresh, as before.
 */
type WorkbookLoad =
  | { kind: "awaiting" }
  | { kind: "ready"; rows: ExecKpiRow[]; truncated: boolean };
const EXEC_REUSE_MS = 5 * 60_000;
/** cache -> tenant -> when its settled answer was read. */
const execReadAt = new WeakMap<LiveCache<WorkbookLoad>, Map<string, number>>();
function noteExecRead(cache: LiveCache<WorkbookLoad>, tenantId: string): void {
  let m = execReadAt.get(cache);
  if (!m) execReadAt.set(cache, (m = new Map()));
  m.set(tenantId, Date.now());
}
/** True when this tenant's answer is settled and was read recently enough to redraw from. */
function execReusable(cache: LiveCache<WorkbookLoad>, tenantId: string): boolean {
  const at = execReadAt.get(cache)?.get(tenantId);
  return at !== undefined && Date.now() - at < EXEC_REUSE_MS && cache.peek(tenantId) !== undefined;
}
const workbookCache = new LiveCache<WorkbookLoad>();
function loadWorkbook(tenantId: string): Promise<WorkbookLoad> {
  return workbookCache.get(tenantId, async () => {
    // AJ 2026-10-01 (speed): the line sources are read alongside the workbook,
    // not after it. A failure is handled in withLineSources (never unhandled).
    const sourcesRead = fetchExecLineSourceRows(tenantId);
    sourcesRead.catch(() => undefined);
    const result = await fetchExecKpiRows(tenantId);
    noteExecRead(workbookCache, tenantId);
    if (result.rows.length === 0) return { kind: "awaiting" };
    if (result.truncated) {
      console.warn(
        `fetchExecKpiRows: stopped after ${result.pages} full pages; ` +
          `showing the first ${result.rows.length} rows of a larger workbook`,
      );
    }
    const rows = await withLineSources(sourcesRead, result.rows);
    return { kind: "ready", rows, truncated: result.truncated };
  });
}
async function refreshWorkbook(tenantId: string): Promise<void> {
  await workbookCache.refreshTenant(tenantId);
}

const derivedCache = new LiveCache<WorkbookLoad>();
function loadDerived(tenantId: string): Promise<WorkbookLoad> {
  return derivedCache.get(tenantId, async () => {
    const result = await fetchExecDerivedRows(tenantId);
    noteExecRead(derivedCache, tenantId);
    if (result.tableMissing || result.rows.length === 0) return { kind: "awaiting" };
    return { kind: "ready", rows: derivedToExecRows(result.rows), truncated: result.truncated };
  });
}
async function refreshDerived(tenantId: string): Promise<void> {
  await derivedCache.refreshTenant(tenantId);
}

const ROLE_LABELS: Record<string, string> = {
  volume: "Volume",
  income: "Total Income",
  opex: "Total OpEx",
  profit: "Profit After OpEx",
  margin: "Profitability",
};

const BLOCK_DOMAIN: Record<string, string> = {
  "Operational Data": "operations",
  "Financial Data": "financial",
  "Operational KPIs": "operations",
  "Financial KPIs": "financial",
};

/** The latest column's wash: the block's domain wash, as KPITile's chips use it. */
function washVar(domain: string): string {
  return `var(--domain-${domain}-wash)`;
}

// Built from code points so prettier cannot unescape them into non-ASCII source.
const UP_ARROW = String.fromCharCode(0x25b2);
const DOWN_ARROW = String.fromCharCode(0x25bc);

const PANEL: CSSProperties = {
  background: "var(--panel)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  boxShadow: "var(--shadow-card)",
};

const EYEBROW: CSSProperties = {
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--text-2)",
  fontWeight: 600,
};

const MONO: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontVariantNumeric: "tabular-nums",
};

const BUTTON: CSSProperties = {
  font: "inherit",
  fontSize: 12.5,
  padding: "5px 11px",
  border: "1px solid var(--border)",
  borderRadius: 6,
  background: "var(--panel)",
  color: "var(--text-2)",
  cursor: "pointer",
};

function Badge(props: { children: ReactNode; title?: string }) {
  return (
    <span
      title={props.title}
      style={{
        ...MONO,
        fontSize: 11,
        color: "var(--text-2)",
        background: "var(--panel)",
        border: "1px solid var(--border)",
        padding: "5px 10px",
        borderRadius: 6,
        whiteSpace: "nowrap",
      }}
    >
      {props.children}
    </span>
  );
}

/** Blob + anchor: this is the app, not the artifact sandbox. */
function downloadCsv(filename: string, text: string): void {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function useElementWidth(ref: RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(() => update());
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/**
 * The routed page. `source` picks the board: "workbook" (the default) reads
 * the client's own workbook with the per-line source switch; "derived" reads
 * the Meridian-derived board (public.exec_derived) for a tenant whose
 * dashboard override sets `executive_page: derived`: Best Disposal (no
 * workbook, built from its imports) and, from AJ 2026-09-30, Delta (built
 * over its own workbook's lines, with no workbook figure on the page). Both
 * render the same ExecutiveBody, so the layout is identical.
 */
export default function Executive(props: {
  divisions?: string[];
  source?: "workbook" | "derived";
}) {
  if (props.source === "derived") return <DerivedExecutive divisions={props.divisions} />;
  return <WorkbookExecutive divisions={props.divisions} />;
}

function WorkbookExecutive(props: { divisions?: string[] }) {
  const tenantId = useTenantId();
  const { session } = useAuth();
  const staff = isStaff(session);
  const availability = useExecKpiAvailability();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  // Joined so the grouping re-runs on a real change of the list, not on
  // every parent render handing down a fresh array with the same contents.
  const divisionKey = (props.divisions ?? []).join(",");

  // The fetch. Re-runs on the probe's answer and on a tenant change only;
  // the division order never triggers a re-read. A live refresh (version)
  // redraws from the refreshed cache with the old figures up meanwhile.
  const version = useLiveCacheVersion(workbookCache);
  const lastRead = useRef<string | null>(null);
  useEffect(() => {
    const readKey = `${availability}|${tenantId}`;
    const refresh = lastRead.current === readKey;
    lastRead.current = readKey;
    if (availability === "pending") {
      setState({ kind: "loading" });
      return;
    }
    if (availability === "missing" || availability === "empty") {
      setState({ kind: "awaiting" });
      return;
    }
    if (!tenantId) {
      // A tenant change passes through null while the slug resolves; the
      // previous tenant's workbook must not stay on screen meanwhile.
      setState({ kind: "loading" });
      return;
    }
    let cancelled = false;
    if (!refresh && !execReusable(workbookCache, tenantId)) {
      workbookCache.delete(tenantId);
      setState({ kind: "loading" });
    }
    loadWorkbook(tenantId)
      .then((load) => {
        if (!cancelled) setState(load);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ExecKpiFetchError && err.tableMissing) {
          setState({ kind: "awaiting" });
          return;
        }
        // The detail is for the console; the page shows fixed copy.
        console.warn(
          `fetchExecKpiRows failed: ${err instanceof Error ? err.message : String(err)}`,
        );
        setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [availability, tenantId, version]);

  // The grouping. Pure, on the rows already fetched and the manifest's
  // division order.
  const data = useMemo<ExecKpiGrouped | null>(() => {
    if (state.kind !== "ready") return null;
    const divisionOrder = divisionKey.split(",").filter(Boolean);
    return groupRows(state.rows, { divisionOrder });
  }, [state, divisionKey]);

  // AJ 2026-09-30: "Updated @" from the last successful EXEC_KPI sync; a
  // newer one re-reads the workbook in place (useLiveReport).
  const updated = useLiveReport(executiveEntity("workbook"), refreshWorkbook);

  return (
    <main style={{ padding: "24px 32px" }}>
      <PageHeader data={data} updated={updated} />
      {state.kind === "loading" ? (
        <Skeleton label="Loading the executive workbook" height={160} />
      ) : state.kind === "awaiting" ? (
        <EmptyState title="Executive KPIs" caption={AWAITING_WORKBOOK_COPY} height={180} />
      ) : state.kind === "error" ? (
        <ErrorState title="Executive KPIs unavailable" message={WORKBOOK_UNAVAILABLE_COPY} />
      ) : data ? (
        <>
          {state.truncated ? <TruncatedBanner rowCount={state.rows.length} /> : null}
          <ExecutiveBody data={data} drillTenantId={tenantId} staff={staff} />
        </>
      ) : null}
    </main>
  );
}

type DerivedState =
  | { kind: "loading" }
  | { kind: "awaiting" }
  | { kind: "error" }
  | { kind: "ready"; rows: ExecKpiRow[]; truncated: boolean };

/**
 * The Meridian-derived board: the Executive page for a tenant whose override
 * sets `executive_page: derived`. Same header, sheet tabs, block pills,
 * cards, trend and table as the workbook page; every figure is Meridian's
 * ("From your imports"), a line with none names what it waits for, and the
 * header stamp says so instead of "workbook received". No workbook read, no
 * workbook probe, no card drill (the drill compares against a workbook
 * figure, which this page never shows).
 */
function DerivedExecutive(props: { divisions?: string[] }) {
  const tenantId = useTenantId();
  const { session } = useAuth();
  const staff = isStaff(session);
  const [state, setState] = useState<DerivedState>({ kind: "loading" });
  const divisionKey = (props.divisions ?? []).join(",");
  // AJ 2026-09-30: "Updated @" from the last successful EXEC_DERIVED sync; a
  // newer one re-reads the board in place (useLiveReport).
  const updated = useLiveReport(executiveEntity("derived"), refreshDerived);
  const version = useLiveCacheVersion(derivedCache);
  const lastTenant = useRef<string | null>(null);

  useEffect(() => {
    const refresh = tenantId !== null && lastTenant.current === tenantId;
    lastTenant.current = tenantId;
    if (!tenantId) {
      setState({ kind: "loading" });
      return;
    }
    let cancelled = false;
    if (!refresh && !execReusable(derivedCache, tenantId)) {
      derivedCache.delete(tenantId);
      setState({ kind: "loading" });
    }
    loadDerived(tenantId)
      .then((load) => {
        if (!cancelled) setState(load);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.warn(
          `fetchExecDerivedRows failed: ${err instanceof Error ? err.message : String(err)}`,
        );
        setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, version]);

  const data = useMemo<ExecKpiGrouped | null>(() => {
    if (state.kind !== "ready") return null;
    const divisionOrder = divisionKey.split(",").filter(Boolean);
    return groupRows(state.rows, { divisionOrder });
  }, [state, divisionKey]);

  return (
    <main style={{ padding: "24px 32px" }}>
      <PageHeader data={data} stamp={DERIVED_STAMP} updated={updated} />
      {state.kind === "loading" ? (
        <Skeleton label="Loading the executive page" height={160} />
      ) : state.kind === "awaiting" ? (
        <EmptyState title="Executive KPIs" caption={AWAITING_IMPORTS_COPY} height={180} />
      ) : state.kind === "error" ? (
        <ErrorState title="Executive KPIs unavailable" message={DERIVED_UNAVAILABLE_COPY} />
      ) : data ? (
        <>
          {state.truncated ? <TruncatedBanner rowCount={state.rows.length} /> : null}
          <ExecutiveBody data={data} staff={staff} derived />
        </>
      ) : null}
    </main>
  );
}

function TruncatedBanner(props: { rowCount: number }) {
  return (
    <div
      role="status"
      style={{
        ...MONO,
        fontSize: 12,
        color: "var(--flag-yellow)",
        background: "var(--flag-yellow-wash)",
        border: "1px solid var(--flag-yellow)",
        borderRadius: 8,
        padding: "8px 12px",
        marginBottom: 14,
      }}
    >
      {truncatedCopy(props.rowCount)}
    </div>
  );
}

/**
 * MER-708: the card drill's load state for one sheet and month.
 *
 * "missing" is the table not being in the database yet (migration 0071 ships
 * separately from the code that fills it); "error" is any other failure and
 * never carries the database message onto the page (the MER-464 rule).
 */
type DrillState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; index: Map<string, DrillEntry> }
  | { kind: "missing" }
  | { kind: "error" };

/**
 * The ready state: sheet buttons, block pills, scorecard, trend and table.
 * A named export (the default stays the routed page) so a render smoke can
 * mount it on a synthetic workbook without the fetch.
 */
export function ExecutiveBody(props: {
  data: ExecKpiGrouped;
  drillTenantId?: string | null;
  staff?: boolean;
  /** The Meridian-derived board (no workbook): every line is ours. */
  derived?: boolean;
}) {
  const { data, drillTenantId = null, staff = false, derived = false } = props;
  const [sheetKey, setSheetKey] = useState<string | null>(null);
  const [block, setBlock] = useState<string>(BLOCK_ORDER[0]);
  const [metric, setMetric] = useState<string | null>(null);
  // MER-708: the card whose customer detail is open, by workbook label.
  const [openLine, setOpenLine] = useState<string | null>(null);
  const [drillState, setDrillState] = useState<DrillState>({ kind: "idle" });

  const sheet: ExecKpiSheet | null = useMemo(() => {
    if (data.sheets.length === 0) return null;
    return data.sheets.find((s) => s.sheet === sheetKey) ?? data.sheets[0];
  }, [data, sheetKey]);
  const blockItems: ExecKpiItem[] = useMemo(
    () => sheet?.blocks.find((b) => b.block === block)?.items ?? [],
    [sheet, block],
  );
  const metricItem: ExecKpiItem | null = useMemo(
    () => blockItems.find((it) => it.lineItem === metric) ?? blockItems[0] ?? null,
    [blockItems, metric],
  );
  // Staleness is against the workbook's own latest period, not the sheet's:
  // a whole sheet that stopped a month early is stale on every card.
  const workbookLatest = data.workbookLatestPeriod ?? data.latest;
  const domain = BLOCK_DOMAIN[block] ?? "operations";

  function pickSheet(next: string) {
    setSheetKey(next);
    setMetric(null);
    setOpenLine(null);
  }
  function pickBlock(next: string) {
    setBlock(next);
    setMetric(null);
  }

  // MER-708: one read per sheet and month, not one per click. A sheet's
  // slice is every line's total row plus the customer rows of the two or
  // three lines that carry detail, so the cards can be marked before anyone
  // clicks and the panel opens without a second request.
  const drillSheet = sheet?.sheet ?? null;
  useEffect(() => {
    if (!drillTenantId || !drillSheet || !workbookLatest) {
      setDrillState({ kind: "idle" });
      return;
    }
    let cancelled = false;
    setDrillState({ kind: "loading" });
    fetchBoardDrill(drillTenantId, drillSheet, workbookLatest)
      .then((result) => {
        if (cancelled) return;
        setDrillState({ kind: "ready", index: indexDrill(result.rows as BoardDrillRow[]) });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const missing = err instanceof BoardDrillFetchError && err.tableMissing;
        console.warn(
          `board_drill read failed: ${err instanceof Error ? err.message : String(err)}`,
        );
        setDrillState({ kind: missing ? "missing" : "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [drillTenantId, drillSheet, workbookLatest]);

  if (!sheet) {
    return <EmptyState title="Executive KPIs" caption={AWAITING_WORKBOOK_COPY} height={180} />;
  }

  return (
    <>
      <div role="group" aria-label="Workbook sheet" style={sheetBarStyle}>
        {data.sheets.map((s) => (
          <button
            key={s.sheet}
            type="button"
            aria-pressed={s.sheet === sheet.sheet}
            onClick={() => pickSheet(s.sheet)}
            style={sheetButtonStyle(s.sheet === sheet.sheet)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div
        role="group"
        aria-label="Block"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          margin: "14px 0",
        }}
      >
        <span style={{ ...EYEBROW, marginRight: 4 }}>Block</span>
        {BLOCK_ORDER.map((b) => (
          <button
            key={b}
            type="button"
            aria-pressed={b === block}
            onClick={() => pickBlock(b)}
            style={pillStyle(b === block)}
          >
            {b}
          </button>
        ))}
        <span style={{ ...MONO, marginLeft: "auto", fontSize: 11, color: "var(--text-muted)" }}>
          {sheet.label}
          {(() => {
            // The derived board counts lines that hold a figure; every line
            // there is Meridian's, so "from your imports" would count all.
            if (derived) {
              const live = sheet.items.filter((it) => !it.awaitingReason).length;
              return ` \u00b7 ${live} of ${sheet.items.length} lines live`;
            }
            const counts = sourceCounts(sheet.items);
            return ` \u00b7 ${counts.ours} of ${sheet.items.length} lines from your imports`;
          })()}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(196px, 1fr))",
          gap: 12,
          marginBottom: 18,
        }}
      >
        {heroCards(sheet, data.months, workbookLatest).map((card) => {
          const entry = drillEntry(drillState, card.item?.lineItem ?? null);
          return (
            <HeroCardView
              key={card.role}
              card={card}
              drillable={isDrillable(entry)}
              drillNote={cardNote(entry)}
              open={openLine !== null && openLine === card.item?.lineItem}
              onOpen={
                card.item
                  ? () =>
                      setOpenLine((current) =>
                        current === card.item?.lineItem ? null : (card.item?.lineItem ?? null),
                      )
                  : undefined
              }
            />
          );
        })}
      </div>

      <DrillPanelView
        state={drillState}
        sheet={sheet}
        openLine={openLine}
        period={workbookLatest}
        staff={staff}
        onClose={() => setOpenLine(null)}
      />

      <section style={{ ...PANEL, padding: "16px 18px", marginBottom: 18 }}>
        <div style={panelHeaderStyle}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
            {block}: {metricItem ? metricItem.lineItem : "Trend"}
          </h3>
          {metricItem?.note ? (
            <span style={{ ...MONO, fontSize: 11, color: "var(--text-muted)" }}>
              {metricItem.note}
            </span>
          ) : null}
          <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={EYEBROW}>Metric</span>
            <select
              value={metricItem?.lineItem ?? ""}
              onChange={(ev) => setMetric(ev.target.value)}
              style={{
                font: "inherit",
                fontSize: 13,
                padding: "5px 9px",
                border: "1px solid var(--border)",
                borderRadius: 6,
                background: "var(--panel)",
                color: "var(--text)",
                maxWidth: "100%",
              }}
            >
              {blockItems.map((it) => (
                <option key={it.lineItem} value={it.lineItem}>
                  {it.lineItem}
                </option>
              ))}
            </select>
          </label>
        </div>
        <TrendChart item={metricItem} months={data.months} domain={domain} />
      </section>

      <section style={{ ...PANEL, padding: "16px 18px" }}>
        <div style={panelHeaderStyle}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{block}: full detail</h3>
          <span
            style={{
              ...MONO,
              fontSize: 11,
              color: "var(--text-2)",
              border: "1px solid var(--border)",
              borderRadius: 999,
              padding: "2px 9px",
            }}
          >
            {blockItems.length} metric{blockItems.length === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            style={{ ...BUTTON, marginLeft: "auto" }}
            onClick={() =>
              downloadCsv(csvFileName(sheet.label, block), blockCsv(sheet, block, data.months))
            }
          >
            Export CSV
          </button>
        </div>
        <DetailTable
          items={blockItems}
          months={data.months}
          selected={metricItem?.lineItem ?? null}
          onSelect={(name) => setMetric(name)}
          domain={domain}
          staff={staff}
          derived={derived}
        />
      </section>

      {derived ? (
        <p
          style={{
            ...MONO,
            fontSize: 11,
            color: "var(--text-muted)",
            lineHeight: 1.8,
            marginTop: 20,
          }}
        >
          Every figure on this page is computed by Meridian from the data you upload and connect (
          <b>{SOURCE_LABELS.ours}</b>): totals are summed, and a ratio is the total of its top line
          over the total of its bottom line, never an average of monthly ratios. Only complete
          months are shown, so the latest month and every month-over-month change compare two full
          months. A line whose source is not connected yet reads &quot;Awaiting&quot; and names what
          it needs; a line Meridian has not built yet reads &quot;Not yet mapped&quot;; a line your
          own workbook defines differently from Meridian&apos;s figure reads &quot;Definition
          differs&quot;. A month with no figure shows &quot;-&quot; with the reason on hover.
          Nothing here is a target, and no missing figure is ever shown as zero.
        </p>
      ) : null}

      {derived ? null : (
        <p
          style={{
            ...MONO,
            fontSize: 11,
            color: "var(--text-muted)",
            lineHeight: 1.8,
            marginTop: 20,
          }}
        >
          Every line says where its figures come from. A line marked <b>{SOURCE_LABELS.ours}</b> is
          computed by Meridian from the data you upload and connect: totals are summed, and a ratio
          is the total of its top line over the total of its bottom line, never an average of
          monthly ratios. A line marked <b>{SOURCE_LABELS.workbook}</b> is your own executive
          workbook figure, shown until Meridian computes that line from your imports; the two are
          never mixed inside a line. Units are declared per line item. A cell with no figure shows
          &quot;-&quot;; a workbook cell marked #DIV/0! or #VALUE! shows n/a with the raw text on
          hover. TTM sums additive figures only; a ratio reads &quot;ratio&quot;. Month-over-month
          colour reads direction: a rise in a cost is red, a rise in profit or income is green, and
          a plain count carries no colour. Targets are the numbers the workbook keeps in its note
          column; nothing else is added.
        </p>
      )}
    </>
  );
}

const sheetBarStyle: CSSProperties = {
  display: "flex",
  borderBottom: "1px solid var(--border)",
  overflowX: "auto",
  gap: 2,
};

function sheetButtonStyle(pressed: boolean): CSSProperties {
  return {
    font: "inherit",
    fontSize: 13.5,
    background: "none",
    border: 0,
    borderBottom: pressed ? "2px solid var(--brand-teal)" : "2px solid transparent",
    padding: "10px 16px",
    color: pressed ? "var(--text)" : "var(--text-2)",
    fontWeight: pressed ? 600 : 400,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
}

function pillStyle(pressed: boolean): CSSProperties {
  return {
    font: "inherit",
    fontSize: 12.5,
    border: pressed ? "1px solid var(--brand-deep)" : "1px solid var(--border)",
    background: pressed ? "var(--brand-deep)" : "var(--panel)",
    color: pressed ? "var(--panel)" : "var(--text-2)",
    borderRadius: 999,
    padding: "4px 12px",
    cursor: "pointer",
    fontWeight: pressed ? 500 : 400,
  };
}

const panelHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 12,
  flexWrap: "wrap",
};

function PageHeader(props: {
  data: ExecKpiGrouped | null;
  stamp?: string;
  /** AJ 2026-09-30: the board's "Updated @" stamp from sync_log. */
  updated?: LiveTabUpdatedAt | null;
}) {
  const { data, stamp, updated } = props;
  const latest = data ? (data.workbookLatestPeriod ?? data.latest) : null;
  const received = data ? formatReceived(data.receivedAt) : null;
  return (
    <header
      style={{
        padding: "22px 28px",
        background:
          "linear-gradient(110deg, var(--brand-mint) 0%, var(--brand-line) 55%, var(--panel) 100%)",
        border: "1px solid var(--brand-line)",
        borderRadius: 8,
        marginBottom: 18,
        display: "flex",
        alignItems: "flex-end",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div>
        <div style={{ ...EYEBROW, fontSize: 11, letterSpacing: 1, fontWeight: 700 }}>
          Board scorecard
        </div>
        <h1
          style={{
            margin: "6px 0 0 0",
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: -0.5,
            color: "var(--text)",
          }}
        >
          Executive
        </h1>
      </div>
      {latest || updated ? (
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {latest ? (
            <Badge>
              Latest: <b style={{ color: "var(--text)" }}>{monthLabel(latest)}</b>
            </Badge>
          ) : null}
          {!latest ? null : stamp ? (
            <Badge>{stamp}</Badge>
          ) : received ? (
            <Badge>workbook received {received}</Badge>
          ) : null}
          {updated ? (
            <Badge title={updated.title}>
              {chipWords(updated).prefix}
              <b style={{ color: "var(--text)" }}>{chipWords(updated).time}</b>
            </Badge>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}

/**
 * MER-708: the customer detail below the cards.
 *
 * Deliberately NOT called a breakdown. The card is the client's own workbook
 * figure and carries no customer grain; this is Meridian's own rows for the
 * same division, line of business and month, with the two totals set side by
 * side. The caveat under the table says exactly that, every time.
 *
 * The workbook value it compares against is read for the PANEL's month, not
 * the card's headline month, so the two sides are always the same month even
 * when a sheet stopped reporting early.
 */
function DrillPanelView(props: {
  state: DrillState;
  sheet: ExecKpiSheet;
  openLine: string | null;
  period: string | null;
  staff?: boolean;
  onClose: () => void;
}) {
  const { state, sheet, openLine, period, staff = false, onClose } = props;
  if (openLine === null || period === null) return null;
  if (state.kind === "loading" || state.kind === "idle") {
    return (
      <section style={{ ...PANEL, padding: "16px 18px", marginBottom: 18 }}>
        <Skeleton height={120} />
      </section>
    );
  }
  if (state.kind === "missing" || state.kind === "error") {
    return (
      <section style={{ ...PANEL, padding: "16px 18px", marginBottom: 18 }}>
        <EmptyState
          title={state.kind === "missing" ? NOT_LOADED_TITLE : READ_FAILED_TITLE}
          caption={state.kind === "missing" ? NOT_LOADED_CAPTION : READ_FAILED_CAPTION}
          height={110}
        />
      </section>
    );
  }
  const entry = state.index.get(openLine) ?? null;
  const item = sheet.blocks.flatMap((b) => b.items).find((it) => it.lineItem === openLine) ?? null;
  // S1-exec-from-imports: the drill compares Meridian's rows with the
  // WORKBOOK figure. On a line the card shows from the imports, that workbook
  // figure is the staff check, so a client session gets the rows and the
  // Meridian total without the workbook rows beneath them.
  const oursLine = item?.source === "ours";
  const hideWorkbook = oursLine && !staff;
  const cardValue = item
    ? oursLine
      ? staff
        ? (item.workbookValues[period] ?? null)
        : null
      : itemValue(item, period)
    : null;
  const panel: DrillPanel = drillPanel(entry, cardValue, period);
  const note = hideWorkbook ? null : differenceNote(panel);
  return (
    <section style={{ ...PANEL, padding: "16px 18px", marginBottom: 18 }}>
      <div style={panelHeaderStyle}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
          {openLine}: {panel.title}
        </h3>
        <span style={{ ...MONO, fontSize: 11, color: "var(--text-muted)" }}>
          {panel.monthLabel}
        </span>
        <button type="button" style={{ ...BUTTON, marginLeft: "auto" }} onClick={onClose}>
          Close
        </button>
      </div>
      {panel.kind !== "detail" ? (
        <p style={{ ...MONO, fontSize: 12, color: "var(--text-2)", lineHeight: 1.7 }}>
          {panel.reason ?? panel.caption}
        </p>
      ) : (
        <>
          {panel.basisLabel ? (
            <p style={{ ...MONO, fontSize: 11, color: "var(--text-2)", margin: "0 0 10px" }}>
              {panel.basisLabel}
              {panel.basisNote ? `. ${panel.basisNote}` : ""}
              {panel.scopeNote ? ` ${panel.scopeNote}` : ""}
            </p>
          ) : null}
          {panel.rows.length === 0 ? (
            <p style={{ ...MONO, fontSize: 12, color: "var(--text-2)", lineHeight: 1.7 }}>
              {panel.caption ?? "Meridian holds no rows for this scope and month."}
            </p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr>
                  <th scope="col" style={drillHeadStyle}>
                    Customer
                  </th>
                  <th scope="col" style={{ ...drillHeadStyle, textAlign: "right" }}>
                    Meridian value
                  </th>
                </tr>
              </thead>
              <tbody>
                {panel.rows.map((row, ri) => (
                  <tr key={row.key} style={ri % 2 === 1 ? drillStripedRowStyle : undefined}>
                    <td
                      style={{
                        ...drillCellStyle,
                        color: row.isRemainder ? "var(--text-muted)" : "var(--text)",
                      }}
                    >
                      {row.name}
                    </td>
                    <td style={{ ...drillCellStyle, ...MONO, textAlign: "right" }}>
                      {row.valueText ?? "n/a"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...drillFootStyle }}>
                    {TOTAL_ROW_LABEL}
                    {panel.customerCount !== null
                      ? ` (${panel.customerCount.toLocaleString("en-US")} customers)`
                      : ""}
                  </td>
                  <td style={{ ...drillFootStyle, ...MONO, textAlign: "right" }}>
                    {panel.ourTotalText ?? "n/a"}
                  </td>
                </tr>
                {hideWorkbook ? null : (
                  <>
                    <tr>
                      <td style={drillFootStyle}>{CARD_ROW_LABEL}</td>
                      <td style={{ ...drillFootStyle, ...MONO, textAlign: "right" }}>
                        {panel.cardText ?? "n/a"}
                      </td>
                    </tr>
                    <tr>
                      <td style={drillFootStyle}>{DIFFERENCE_ROW_LABEL}</td>
                      <td style={{ ...drillFootStyle, ...MONO, textAlign: "right" }}>
                        {panel.differenceText ?? "n/a"}
                        {panel.differencePctText ? ` (${panel.differencePctText})` : ""}
                      </td>
                    </tr>
                  </>
                )}
              </tfoot>
            </table>
          )}
          <p
            style={{
              ...MONO,
              fontSize: 11,
              color: "var(--text-muted)",
              lineHeight: 1.8,
              marginTop: 14,
              marginBottom: 0,
            }}
          >
            {oursLine ? OURS_DRILL_CAVEAT : DRILL_CAVEAT}
            {note ? ` ${note}` : ""}
          </p>
        </>
      )}
    </section>
  );
}

/** S1-exec-from-imports: the drill caveat on a card that shows Meridian's figure. */
const OURS_DRILL_CAVEAT =
  "This card shows Meridian's figure from your imports. The table is Meridian's own rows for the same division, line of business and month, customer by customer; its total can differ slightly from the card because the card is read from the monthly summary.";

// MER-377: brand green-to-teal bar under the header row + bold labels, matching
// the tabular-widget and pivot tables everywhere else on the dashboard.
const drillHeadStyle: CSSProperties = {
  ...EYEBROW,
  textAlign: "left",
  padding: "6px 8px",
  borderBottom: "2px solid var(--brand-teal)",
};

const drillCellStyle: CSSProperties = {
  padding: "6px 8px",
  borderBottom: "0.5px solid var(--border)",
};

// MER-377: faint alternating row tint, same treatment as PresentedTable.
const drillStripedRowStyle: CSSProperties = { background: "var(--bg-app)" };

const drillFootStyle: CSSProperties = {
  padding: "7px 8px",
  borderTop: "1px solid var(--border)",
  fontWeight: 600,
};

/** The drill entry for one card's line, or null when nothing is loaded. */
function drillEntry(state: DrillState, lineItem: string | null): DrillEntry | null {
  if (state.kind !== "ready" || lineItem === null) return null;
  return state.index.get(lineItem) ?? null;
}

function HeroCardView(props: {
  card: HeroCardData;
  drillable?: boolean;
  drillNote?: string | null;
  open?: boolean;
  onOpen?: () => void;
}) {
  const { card, drillable = false, drillNote = null, open = false, onOpen } = props;
  const { item, delta, stale } = card;
  // MER-571, as the Overview drill already applies it: a card that cannot
  // open a detail panel must not look like a door. Only a line whose detail
  // the pipeline actually built is clickable.
  const clickable = drillable && typeof onOpen === "function";
  const title = item ? item.lineItem : ROLE_LABELS[card.role];
  const value = item && delta ? fmtValue(delta.curV, item.unit, item.decimals) : null;
  // good / bad carry colour; flat and neutral (a plain count) stay grey.
  const toneColor =
    delta?.tone === "good"
      ? "var(--flag-green)"
      : delta?.tone === "bad"
        ? "var(--flag-red)"
        : "var(--text-muted)";
  const arrow =
    delta?.direction === "up" ? UP_ARROW : delta?.direction === "down" ? DOWN_ARROW : "";
  const body = (
    <>
      <header style={{ ...EYEBROW, minHeight: "2.7em", lineHeight: 1.35 }}>{title}</header>
      <div
        style={{
          ...MONO,
          fontSize: value ? 22 : 12,
          fontWeight: 600,
          color: value ? "var(--text-primary)" : "var(--text-muted)",
          letterSpacing: value ? -0.4 : 0,
          marginTop: 4,
          // A derived line with no figure names what it waits for, which can
          // run longer than a figure; it wraps inside the card.
          whiteSpace: value ? "nowrap" : "normal",
        }}
      >
        {value ??
          (item
            ? item.awaitingReason
              ? (item.awaitingLabel ?? AWAITING_CARD_COPY)
              : "n/a"
            : "Not on this sheet")}
      </div>
      <div
        style={{
          ...MONO,
          fontSize: 11,
          marginTop: 4,
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
          color: "var(--text-muted)",
        }}
      >
        {delta && delta.prev !== null ? (
          <>
            {delta.pct !== null ? (
              <span style={{ color: toneColor, fontWeight: 600 }}>
                {arrow ? `${arrow} ` : ""}
                {Math.abs(delta.pct).toFixed(1)}%
              </span>
            ) : (
              <span>prior month was zero</span>
            )}
            <span>
              {monthLabel(delta.cur)} vs {monthLabel(delta.prev)}
            </span>
          </>
        ) : delta ? (
          <span>no prior month</span>
        ) : item?.awaitingReason ? (
          item.awaitingLabel && item.awaitingReason.startsWith(item.awaitingLabel) ? null : (
            <span>{item.awaitingReason}</span>
          )
        ) : item ? (
          <span>no data</span>
        ) : null}
        {stale && delta ? <span>(as of {monthLabel(delta.cur)})</span> : null}
      </div>
      {item && !item.awaitingReason ? (
        <div
          style={{ ...MONO, fontSize: 10.5, marginTop: 6, color: "var(--text-2)" }}
          title={item.sourceReason ?? undefined}
        >
          {SOURCE_LABELS[item.source]}
        </div>
      ) : null}
      {drillNote ? (
        <div style={{ ...MONO, fontSize: 10.5, marginTop: 6, color: "var(--text-muted)" }}>
          {drillNote}
        </div>
      ) : clickable ? (
        <div style={{ ...MONO, fontSize: 10.5, marginTop: 6, color: "var(--text-2)" }}>
          {open ? "Hide customer detail" : "Show customer detail"}
        </div>
      ) : null}
    </>
  );
  const frame: CSSProperties = {
    background: "var(--panel)",
    border: "0.5px solid var(--border)",
    borderTop: `3px solid ${domainVar(card.domain)}`,
    borderRadius: 6,
    padding: "13px 15px 12px",
    boxShadow: "var(--shadow-tile)",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  };
  if (!clickable) {
    return (
      <article aria-label={`${title}: ${value ?? "n/a"}`} style={frame}>
        {body}
      </article>
    );
  }
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-label={`${title}: ${value ?? "n/a"}. Show the customer detail Meridian holds for this scope.`}
      onClick={onOpen}
      style={{
        ...frame,
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        outline: open ? "2px solid var(--focus, var(--text-2))" : undefined,
        outlineOffset: open ? 1 : undefined,
      }}
    >
      {body}
    </button>
  );
}

const CHART_W = 1000;
const CHART_H = 260;
const CHART_L = 64;
const CHART_R = 20;
const CHART_T = 18;
const CHART_B = 34;
const PLOT_W = CHART_W - CHART_L - CHART_R;
const PLOT_H = CHART_H - CHART_T - CHART_B;

function TrendChart(props: { item: ExecKpiItem | null; months: string[]; domain: string }) {
  const { item, months, domain } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const width = useElementWidth(wrapRef);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    setHover(null);
  }, [item]);

  const model = useMemo(() => {
    if (!item || months.length === 0) return null;
    const xs = months.map((_, i) =>
      months.length === 1 ? CHART_L + PLOT_W / 2 : CHART_L + (i * PLOT_W) / (months.length - 1),
    );
    const series = seriesSegments(item, months);
    const target = parseTarget(item.note, item);
    const scale = chartScale(
      series.points.map((p) => p.v),
      { target, ratio: item.unit === "fraction" || !item.additive },
    );
    if (!scale) return null;
    const y = (v: number) => CHART_T + ((scale.hi - v) / (scale.hi - scale.lo)) * PLOT_H;
    return { xs, series, target, scale, y };
  }, [item, months]);

  if (!item) {
    return (
      <p style={{ ...MONO, fontSize: 11, color: "var(--text-muted)" }}>
        No line items in this block on this sheet.
      </p>
    );
  }
  if (!model || model.series.points.length === 0) {
    return (
      <p style={{ ...MONO, fontSize: 11, color: "var(--text-muted)" }}>
        {item.awaitingReason
          ? awaitingSentence(item.lineItem, item.awaitingLabel, item.awaitingReason)
          : `No values on this sheet for ${item.lineItem}.`}
      </p>
    );
  }

  const { xs, series, target, scale, y } = model;
  const pts = series.points;
  const stroke = domainVar(domain);
  const stride = labelStride(months.length, width > 0 ? Math.max(4, Math.floor(width / 72)) : 14);
  const base = y(scale.baseline);
  // One sub-path per run of populated months: a blank or error month breaks
  // the line rather than bridging it.
  const linePath = series.segments
    .map((seg) =>
      seg.map((p, i) => `${i ? "L" : "M"}${xs[p.i].toFixed(1)},${y(p.v).toFixed(1)}`).join(" "),
    )
    .join(" ");
  const areaPath = series.segments
    .filter((seg) => seg.length > 1)
    .map(
      (seg) =>
        `M${xs[seg[0].i].toFixed(1)},${base.toFixed(1)} ` +
        seg.map((p) => `L${xs[p.i].toFixed(1)},${y(p.v).toFixed(1)}`).join(" ") +
        ` L${xs[seg[seg.length - 1].i].toFixed(1)},${base.toFixed(1)} Z`,
    )
    .join(" ");
  const last = pts[pts.length - 1];
  const hovered = hover !== null ? pts[hover] : null;
  const axisBottom = CHART_H - CHART_B;

  function pickNearest(clientX: number) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0) return;
    const px = ((clientX - rect.left) * CHART_W) / rect.width;
    let best = 0;
    for (let i = 1; i < pts.length; i += 1) {
      if (Math.abs(xs[pts[i].i] - px) < Math.abs(xs[pts[best].i] - px)) best = i;
    }
    setHover(best);
  }
  function onMove(ev: MouseEvent<SVGRectElement>) {
    pickNearest(ev.clientX);
  }
  function onTouchMove(ev: TouchEvent<SVGRectElement>) {
    const touch = ev.touches[0];
    if (touch) pickNearest(touch.clientX);
  }

  return (
    <div>
      <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          role="img"
          aria-label={`${item.lineItem} by month, ${monthLabel(series.first)} to ${monthLabel(series.last)}`}
          style={{ display: "block", width: "100%", height: "auto" }}
        >
          {scale.ticks.map((t) => (
            <g key={t}>
              <line
                x1={CHART_L}
                x2={CHART_W - CHART_R}
                y1={y(t).toFixed(1)}
                y2={y(t).toFixed(1)}
                stroke="var(--border)"
                strokeWidth={1}
              />
              <text
                x={CHART_L - 8}
                y={(y(t) + 3.5).toFixed(1)}
                textAnchor="end"
                fontSize={10}
                fill="var(--text-muted)"
                fontFamily="var(--font-mono)"
              >
                {fmtAxis(t, item.unit)}
              </text>
            </g>
          ))}
          {target ? (
            <g>
              {target.low !== target.high ? (
                <rect
                  x={CHART_L}
                  y={y(target.high).toFixed(1)}
                  width={PLOT_W}
                  height={Math.max(0, y(target.low) - y(target.high)).toFixed(1)}
                  fill="var(--border)"
                  opacity={0.6}
                />
              ) : null}
              <line
                x1={CHART_L}
                x2={CHART_W - CHART_R}
                y1={y(target.high).toFixed(1)}
                y2={y(target.high).toFixed(1)}
                stroke="var(--text-2)"
                strokeWidth={1.5}
                strokeDasharray="5 4"
              />
              {target.low !== target.high ? (
                <line
                  x1={CHART_L}
                  x2={CHART_W - CHART_R}
                  y1={y(target.low).toFixed(1)}
                  y2={y(target.low).toFixed(1)}
                  stroke="var(--text-2)"
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                />
              ) : null}
            </g>
          ) : null}
          {areaPath ? <path d={areaPath} fill={stroke} fillOpacity={0.12} /> : null}
          <path
            d={linePath}
            fill="none"
            stroke={stroke}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {pts.map((p) => (
            <circle
              key={p.m}
              cx={xs[p.i].toFixed(1)}
              cy={y(p.v).toFixed(1)}
              r={2.6}
              fill={stroke}
            />
          ))}
          <circle
            cx={xs[last.i].toFixed(1)}
            cy={y(last.v).toFixed(1)}
            r={5}
            fill={stroke}
            stroke="var(--panel)"
            strokeWidth={2}
          />
          {hovered ? (
            <line
              x1={xs[hovered.i]}
              x2={xs[hovered.i]}
              y1={CHART_T}
              y2={axisBottom}
              stroke="var(--text-muted)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          ) : null}
          {months.map((m, i) =>
            i % stride === 0 || i === months.length - 1 ? (
              <text
                key={m}
                x={xs[i].toFixed(1)}
                y={axisBottom + 18}
                textAnchor="middle"
                fontSize={10}
                fill="var(--text-muted)"
                fontFamily="var(--font-mono)"
              >
                {monthLabel(m)}
              </text>
            ) : null,
          )}
          {/* The hover hit area is the plot only, not the axes or margins. */}
          <rect
            x={CHART_L}
            y={CHART_T}
            width={PLOT_W}
            height={PLOT_H}
            fill="transparent"
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
            onTouchMove={onTouchMove}
          />
          {/* An error month is a hollow marker on the axis, raw text on hover. */}
          {series.errors.map((e) => (
            <circle
              key={e.m}
              cx={xs[e.i].toFixed(1)}
              cy={axisBottom}
              r={4}
              fill="var(--panel)"
              stroke="var(--text-muted)"
              strokeWidth={1.5}
            >
              <title>{`${monthLabel(e.m)}: workbook cell reads ${e.text}`}</title>
            </circle>
          ))}
        </svg>
        {hovered ? (
          <div
            style={{
              ...MONO,
              position: "absolute",
              left: `${(xs[hovered.i] / CHART_W) * 100}%`,
              top: `${(y(hovered.v) / CHART_H) * 100}%`,
              transform: "translate(-50%, -115%)",
              pointerEvents: "none",
              background: "var(--panel)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "6px 9px",
              fontSize: 11,
              color: "var(--text)",
              boxShadow: "var(--shadow-card)",
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ display: "block", color: "var(--text-muted)" }}>
              {monthLabel(hovered.m)}
            </span>
            {fmtValue(hovered.v, item.unit, item.decimals)}
          </div>
        ) : null}
      </div>
      {target || !scale.zeroBased ? (
        <div style={{ ...MONO, fontSize: 10.5, color: "var(--text-muted)", marginTop: 6 }}>
          {target ? (
            <>
              <span
                aria-hidden
                style={{
                  display: "inline-block",
                  width: 14,
                  borderTop: "2px dashed var(--text-2)",
                  verticalAlign: "middle",
                  marginRight: 6,
                }}
              />
              {target.label}, from the workbook note column.{" "}
            </>
          ) : null}
          {!scale.zeroBased
            ? `Axis runs ${fmtAxis(scale.lo, item.unit)} to ${fmtAxis(scale.hi, item.unit)}, not from zero.`
            : null}
        </div>
      ) : null}
    </div>
  );
}

// MER-377: brand green-to-teal bar under the header row, matching the
// tabular-widget and pivot tables everywhere else on the dashboard.
const TH: CSSProperties = {
  ...MONO,
  position: "sticky",
  top: 0,
  padding: "7px 10px",
  fontSize: 9.5,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
  textAlign: "right",
  whiteSpace: "nowrap",
  background: "var(--bg)",
  borderBottom: "2px solid var(--brand-teal)",
  zIndex: 2,
};

const TD: CSSProperties = {
  ...MONO,
  padding: "7px 10px",
  fontSize: 12.5,
  textAlign: "right",
  whiteSpace: "nowrap",
  borderBottom: "1px solid var(--border)",
};

// MER-377: faint alternating row tint on the line-item matrix. The latest
// month's per-cell wash and the sticky first column's own opaque background
// both set an explicit background and so still win over this row-level one.
const stripedRowStyle: CSSProperties = { background: "var(--bg-app)" };

function ProvenanceChip(props: { provenance: string | null; mixed: boolean }) {
  const tag = provenanceTag(props.provenance);
  return (
    <span
      title={props.mixed ? `${tag.title} (varies by month)` : tag.title}
      style={{
        ...MONO,
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: 0.3,
        padding: "1px 6px",
        borderRadius: 3,
        background: "var(--bg)",
        border: "1px solid var(--border)",
        color: "var(--text-2)",
      }}
    >
      {tag.tag}
      {props.mixed ? "*" : ""}
    </span>
  );
}

function rowButtonStyle(selected: boolean): CSSProperties {
  return {
    font: "inherit",
    fontFamily: "var(--font-stack)",
    fontSize: 12.5,
    fontWeight: 500,
    textAlign: "left",
    whiteSpace: "normal",
    background: "none",
    border: 0,
    padding: 0,
    cursor: "pointer",
    color: selected ? "var(--brand-deep)" : "var(--text)",
  };
}

/**
 * S1-exec-from-imports: the line's source, on every row. The provenance tag
 * beside it keeps the workbook's own GL / Ops / Assumed reading on a workbook
 * line, and reads Imports on a line Meridian computes.
 */
function SourceChip(props: { item: ExecKpiItem; staff: boolean }) {
  const { item, staff } = props;
  const ours = item.source === "ours";
  if (item.awaitingReason) {
    // Derived board only: a line whose source is not connected yet, named
    // ("Awaiting QuickBooks P&L by division"), or whose definition differs.
    return (
      <span
        title={item.awaitingReason}
        style={{
          ...MONO,
          display: "inline-block",
          maxWidth: 240,
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: 0.3,
          padding: "1px 6px",
          borderRadius: 3,
          whiteSpace: "normal",
          background: "var(--bg)",
          border: "1px dashed var(--border)",
          color: "var(--text-muted)",
        }}
      >
        {item.awaitingLabel ?? AWAITING_CARD_COPY}
      </span>
    );
  }
  return (
    <span
      title={
        ours
          ? "Computed by Meridian from your imported data"
          : staff && item.sourceReason
            ? `Staff: why not from the imports: ${item.sourceReason}`
            : "Your own executive workbook figure"
      }
      style={{
        ...MONO,
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: 0.3,
        padding: "1px 6px",
        borderRadius: 3,
        whiteSpace: "nowrap",
        background: ours ? "var(--brand-mint)" : "var(--bg)",
        border: "1px solid var(--border)",
        color: ours ? "var(--brand-deep)" : "var(--text-2)",
      }}
    >
      {SOURCE_LABELS[item.source]}
    </span>
  );
}

function DetailTable(props: {
  items: ExecKpiItem[];
  months: string[];
  selected: string | null;
  onSelect: (lineItem: string) => void;
  domain: string;
  staff?: boolean;
  derived?: boolean;
}) {
  const { items, months, selected, onSelect, domain, staff = false, derived = false } = props;
  const latest = months.length ? months[months.length - 1] : null;
  const ttmWindow = months.slice(-TTM_MONTHS);
  const ttmLabel = ttmWindow.length
    ? `TTM ${monthLabel(ttmWindow[0])} to ${monthLabel(ttmWindow[ttmWindow.length - 1])}`
    : "TTM";
  const wash = washVar(domain);

  if (items.length === 0) {
    return <EmptyState title="No line items in this block on this sheet." height={100} />;
  }

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
      <table style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: "100%" }}>
        <thead>
          <tr>
            <th
              scope="col"
              style={{
                ...TH,
                textAlign: "left",
                left: 0,
                zIndex: 3,
                minWidth: 220,
                borderRight: "1px solid var(--border)",
              }}
            >
              Metric
            </th>
            <th
              scope="col"
              style={{ ...TH, textAlign: "left" }}
              title="Whose figures the line shows: Meridian's, from your imports, or your workbook's"
            >
              Source
            </th>
            <th
              scope="col"
              style={{ ...TH, textAlign: "left" }}
              title="Where the figure is reported from"
            >
              Src
            </th>
            {months.map((m) => (
              <th
                key={m}
                scope="col"
                style={{ ...TH, background: m === latest ? wash : "var(--bg)" }}
              >
                {monthLabel(m)}
              </th>
            ))}
            <th
              scope="col"
              style={{ ...TH, borderLeft: "1px solid var(--border)" }}
              title={`Trailing ${TTM_MONTHS} months. Additive figures are summed; a ratio shows the word "ratio".`}
            >
              {ttmLabel}
            </th>
            <th
              scope="col"
              style={{
                ...TH,
                textAlign: "left",
                borderLeft: "1px solid var(--border)",
                minWidth: 220,
              }}
            >
              Note
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, ii) => {
            const isSelected = it.lineItem === selected;
            const t = ttm(it, months);
            const noteFrom = noteFromLabel(it);
            // The staff check is the workbook figure; a derived board has none.
            const checkRow = staff && it.source === "ours" && !derived;
            return (
              <Fragment key={it.lineItem}>
                <tr style={ii % 2 === 1 ? stripedRowStyle : undefined}>
                  <th
                    scope="row"
                    style={{
                      ...TD,
                      fontFamily: "var(--font-stack)",
                      textAlign: "left",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      background: "var(--panel)",
                      fontWeight: 500,
                      whiteSpace: "normal",
                      minWidth: 220,
                      maxWidth: 340,
                      borderRight: "1px solid var(--border)",
                      boxShadow: isSelected ? "inset 3px 0 0 var(--brand-teal)" : undefined,
                    }}
                  >
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => onSelect(it.lineItem)}
                      style={rowButtonStyle(isSelected)}
                    >
                      {it.lineItem}
                    </button>
                  </th>
                  <td style={{ ...TD, textAlign: "left" }}>
                    <SourceChip item={it} staff={staff} />
                  </td>
                  <td style={{ ...TD, textAlign: "left" }}>
                    <ProvenanceChip provenance={it.provenance} mixed={it.provenanceMixed} />
                  </td>
                  {months.map((m) => {
                    const v = itemValue(it, m);
                    const raw = itemCellText(it, m);
                    const cellStyle: CSSProperties = {
                      ...TD,
                      background: m === latest ? wash : undefined,
                      fontWeight: m === latest ? 600 : 400,
                    };
                    if (v !== null) {
                      return (
                        <td key={m} style={cellStyle}>
                          {fmtValue(v, it.unit, it.decimals)}
                        </td>
                      );
                    }
                    if (raw) {
                      return (
                        <td
                          key={m}
                          title={`Workbook cell reads ${raw}`}
                          style={{ ...cellStyle, color: "var(--text-muted)", fontStyle: "italic" }}
                        >
                          n/a
                        </td>
                      );
                    }
                    return (
                      <td
                        key={m}
                        title={
                          derived
                            ? (it.missingReason[m] ?? "No figure from your imports for this month")
                            : it.source === "ours"
                              ? `No figure from your imports for this month${
                                  staff && it.missingReason[m]
                                    ? ` (staff: ${it.missingReason[m]})`
                                    : ""
                                }`
                              : "Blank in the workbook"
                        }
                        style={{ ...cellStyle, color: "var(--text-muted)" }}
                      >
                        -
                      </td>
                    );
                  })}
                  {t === null ? (
                    <td
                      title="A ratio cannot be summed across months"
                      style={{
                        ...TD,
                        borderLeft: "1px solid var(--border)",
                        color: "var(--text-muted)",
                        fontStyle: "italic",
                      }}
                    >
                      ratio
                    </td>
                  ) : (
                    <td
                      title={`${t.n} of ${TTM_MONTHS} months present`}
                      style={{ ...TD, borderLeft: "1px solid var(--border)" }}
                    >
                      {t.value === null ? "-" : fmtValue(t.value, it.unit, it.decimals)}
                      {t.n < TTM_MONTHS ? (
                        <span style={{ marginLeft: 6, fontSize: 10.5, color: "var(--text-muted)" }}>
                          {t.n}/{TTM_MONTHS}
                        </span>
                      ) : null}
                    </td>
                  )}
                  <td
                    title={
                      it.note
                        ? noteFrom
                          ? `${it.note} (note from ${noteFrom})`
                          : it.note
                        : undefined
                    }
                    style={{
                      ...TD,
                      textAlign: "left",
                      fontSize: 10.5,
                      color: "var(--text-muted)",
                      whiteSpace: "normal",
                      minWidth: 220,
                      maxWidth: 360,
                      borderLeft: "1px solid var(--border)",
                    }}
                  >
                    {it.note ?? ""}
                  </td>
                </tr>
                {checkRow ? <StaffCheckRow item={it} months={months} latest={latest} /> : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * S1-exec-from-imports: STAFF ONLY. Under a line shown from the imports, the
 * client's own workbook figure for each month, with our gap from it on hover.
 * It is the check on our number, never a second source for it: it is never
 * rendered for a client session and never feeds a card, a trend or a CSV.
 */
function StaffCheckRow(props: { item: ExecKpiItem; months: string[]; latest: string | null }) {
  const { item, months, latest } = props;
  const muted: CSSProperties = {
    ...TD,
    fontSize: 11,
    color: "var(--text-muted)",
    background: "var(--bg)",
  };
  return (
    <tr>
      <th
        scope="row"
        style={{
          ...muted,
          fontFamily: "var(--font-stack)",
          textAlign: "left",
          position: "sticky",
          left: 0,
          zIndex: 1,
          borderRight: "1px solid var(--border)",
          fontWeight: 400,
          whiteSpace: "normal",
        }}
      >
        Staff check: workbook figure
      </th>
      <td style={{ ...muted, textAlign: "left" }} colSpan={2}>
        not shown to the client
      </td>
      {months.map((m) => {
        const wv = item.workbookValues[m];
        const raw = item.workbookCellText[m];
        const check = staffCheck(item.values[m], wv);
        const gap =
          check && check.gapPct !== null
            ? `ours ${check.gapPct >= 0 ? "+" : ""}${(check.gapPct * 100).toFixed(1)}% vs workbook`
            : undefined;
        return (
          <td key={m} title={gap} style={{ ...muted, fontWeight: m === latest ? 600 : 400 }}>
            {typeof wv === "number" ? fmtValue(wv, item.unit, item.decimals) : raw ? "n/a" : "-"}
          </td>
        );
      })}
      <td style={{ ...muted, borderLeft: "1px solid var(--border)" }} />
      <td style={{ ...muted, borderLeft: "1px solid var(--border)" }} />
    </tr>
  );
}
